import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import RuleFields from 'components/SlateViewsSettings/RuleFields/RuleFields';
import ColumnPreviewTable from 'components/FieldColumns/ColumnPreviewTable';
import SelectedColumnsList from './SelectedColumnsList';

import './ColumnPickerModal.css';

/**
 * Modal body for column picker.
 * Two-panel layout: left is RuleFields, right is SelectedColumnsList, bottom is preview.
 */
var ColumnPickerModal = function(props) {
  var draftFields = props.draftFields;
  var availableFields = props.availableFields;
  var allFields = props.allFields;
  var selectedFields = props.selectedFields;
  var isRecordListView = props.isRecordListView;
  var recordsFieldUniquenessIdentifier = props.recordsFieldUniquenessIdentifier;
  var recordFieldTitle = props.recordFieldTitle;
  var onFieldClick = props.onFieldClick;
  var onFieldDrop = props.onFieldDrop;
  var onRemove = props.onRemove;
  var onRemoveAll = props.onRemoveAll;
  var onReorder = props.onReorder;
  var onBulkAdd = props.onBulkAdd;
  var onAttributeChange = props.onAttributeChange;
  var t = props.t;

  var modalRef = useRef(null);

  var _isMobile = useState(function() {
    return window.matchMedia && window.matchMedia('(max-width: 990px)').matches;
  });
  var isMobile = _isMobile[0];
  var setIsMobile = _isMobile[1];

  useEffect(function() {
    if (!window.matchMedia) return;
    var mql = window.matchMedia('(max-width: 990px)');
    var handler = function(e) {
      setIsMobile(e.matches);
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
    } else if (mql.addListener) {
      mql.addListener(handler);
    }
    return function() {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler);
      } else if (mql.removeListener) {
        mql.removeListener(handler);
      }
    };
  }, []);

  var _searchQuery = useState('');
  var searchQuery = _searchQuery[0];
  var setSearchQuery = _searchQuery[1];

  var _drawerOpen = useState(false);
  var drawerOpen = _drawerOpen[0];
  var setDrawerOpen = _drawerOpen[1];

  var handleOpenDrawer = function() {
    setDrawerOpen(true);
  };

  var handleCloseDrawer = function() {
    setDrawerOpen(false);
  };

  var handleSearchChange = function(e) {
    setSearchQuery(e.target.value);
  };

  var handleClearSearch = function() {
    setSearchQuery('');
  };

  // Prevent all text selection inside the modal via DOM event listener
  useEffect(function() {
    var el = modalRef.current;
    if (!el) return;
    var handleSelectStart = function(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
    };
    el.addEventListener('selectstart', handleSelectStart);
    return function() {
      el.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  var _externalDragging = useState(false);
  var isExternalDragging = _externalDragging[0];
  var setIsExternalDragging = _externalDragging[1];

  // Called when a field drag starts in the left panel (RuleFields/CollapsibleField)
  // Same pattern as old PreviewTablePicker.handleFieldDragStart
  var handleDragStart = function() {
    setIsExternalDragging(true);

    var handleDragEnd = function() {
      setIsExternalDragging(false);
      document.removeEventListener('dragend', handleDragEnd, true);
    };

    // Use capture phase to ensure we catch the event even if it's stopped
    document.addEventListener('dragend', handleDragEnd, true);
  };

  var handleExternalDrop = function(fieldData, insertIndex) {
    if (onFieldDrop) {
      onFieldDrop(fieldData, insertIndex);
    }
    setIsExternalDragging(false);
  };

  var customCount = draftFields.filter(function(r) { return !r.locked; }).length;
  var hasColumns = customCount > 0;

  return (
    <div className="column-picker-modal" ref={modalRef}>
      <div className="column-picker-drawer-toggle" onClick={handleOpenDrawer}>
        <span className="material-symbols-outlined column-picker-drawer-toggle-icon">add</span>
        <span>{t('settings:addColumns')}</span>
      </div>
      <div className="column-picker-panels">
        <div
          className={classnames('column-picker-drawer-backdrop', {
            'column-picker-drawer-backdrop--visible': drawerOpen,
          })}
          onClick={handleCloseDrawer}
        />
        <div className={classnames('column-picker-left-panel', {
          'column-picker-left-panel--open': drawerOpen,
        })}>
          <div className="column-picker-drawer-close" onClick={handleCloseDrawer}>
            <span className="material-symbols-outlined">arrow_back</span>
            <span>{t('settings:addColumns')}</span>
          </div>
          <div className="column-picker-section-header column-picker-search-header-row">
            <i className="material-icons column-picker-search-icon">search</i>
            <input
              type="text"
              className="column-picker-search-header"
              placeholder="Search available fields..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                className="column-picker-search-clear"
                onClick={handleClearSearch}
              >
                <i className="material-icons">close</i>
              </button>
            )}
          </div>
          <RuleFields
            allFields={allFields}
            selectedFields={selectedFields}
            recordsFieldUniquenessIdentifier={recordsFieldUniquenessIdentifier}
            isRecordListView={isRecordListView}
            isColumnPicker={true}
            recordFieldTitle={recordFieldTitle}
            fields={availableFields}
            onFieldClick={onFieldClick}
            onDragStart={isMobile ? undefined : handleDragStart}
            useDoubleClick={true}
            onBulkAdd={onBulkAdd}
            hideSearch
            externalSearchQuery={searchQuery}
            showAddButton={isMobile}
          />
        </div>
        <div className="column-picker-right-panel">
          <div className="column-picker-section-header">
            <span className="column-picker-section-title">
              {hasColumns
                ? customCount + ' ' + (customCount === 1
                  ? t('settings:columnSelected')
                  : t('settings:columnsSelected'))
                : t('settings:noColumnsSelected')
              }
            </span>
            {hasColumns && onRemoveAll && (
              <button
                type="button"
                className="column-picker-remove-all"
                onClick={onRemoveAll}
              >
                {t('settings:removeAll')}
              </button>
            )}
          </div>
          <div className={classnames('column-picker-right-body', {
            'column-picker-right-body--drag-active': isExternalDragging,
          })}>
            <SelectedColumnsList
              fields={draftFields}
              onRemove={onRemove}
              onReorder={onReorder}
              onAttributeChange={onAttributeChange}
              isRecordListView={isRecordListView}
              isExternalDragging={isExternalDragging}
              onExternalDrop={handleExternalDrop}
              isMobile={isMobile}
              t={t}
            />
          </div>
        </div>
      </div>

      <div className="column-picker-preview-wrapper">
        <div className="column-picker-section-header">
          <span className="column-picker-section-title">
            {t('settings:columnPreview')}
          </span>
        </div>
        <ColumnPreviewTable columns={draftFields} t={t} />
      </div>
    </div>
  );
};

ColumnPickerModal.propTypes = {
  draftFields: PropTypes.array.isRequired,
  availableFields: PropTypes.array.isRequired,
  allFields: PropTypes.array,
  selectedFields: PropTypes.array,
  isRecordListView: PropTypes.bool,
  recordsFieldUniquenessIdentifier: PropTypes.string,
  recordFieldTitle: PropTypes.string,
  onFieldClick: PropTypes.func.isRequired,
  onFieldDrop: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onRemoveAll: PropTypes.func,
  onReorder: PropTypes.func.isRequired,
  onBulkAdd: PropTypes.func,
  onAttributeChange: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default ColumnPickerModal;
